/**
 * Breadcrumb utility functions
 *
 * This module provides helper functions for breadcrumb navigation,
 * including entity type detection and label formatting.
 */

import type { WorkspaceItemType } from '@loader/shared-types';

/**
 * UUID pattern matching regex
 * Matches standard UUID v4 format: 8-4-4-4-12 hexadecimal digits
 */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Entity types that can be detected in breadcrumb paths
 */
export type BreadcrumbEntityType = 'table' | 'linkedTable' | 'version' | 'schema' | 'folder';

/**
 * Breadcrumb item interface
 */
export interface BreadcrumbItemData {
  label: string;
  href?: string;
  isLoading?: boolean;
  iconType?: 'workspace' | 'trash' | WorkspaceItemType;
}

/**
 * Determine entity type from path segment position and context
 *
 * @param pathSegments - Array of path segments from the URL
 * @param segmentIndex - Index of the segment to check
 * @returns The detected entity type or null if not a recognized entity
 */
export function getEntityType(
  pathSegments: string[],
  segmentIndex: number
): BreadcrumbEntityType | null {
  const segment = pathSegments[segmentIndex];
  const prevSegment = segmentIndex > 0 ? pathSegments[segmentIndex - 1] : null;

  // Check if this is a UUID
  if (!UUID_PATTERN.test(segment)) {
    return null;
  }

  // Determine entity type based on path structure
  if (prevSegment === 'tables') {
    // Check if next segment indicates a specific page
    const nextSegment = pathSegments[segmentIndex + 1];
    if (nextSegment === 'version') {
      return 'version';
    }
    return 'table';
  }

  if (prevSegment === 'linkedTables') {
    return 'linkedTable';
  }

  if (prevSegment === 'schemas') {
    return 'schema';
  }

  if (prevSegment === 'folder') {
    return 'folder';
  }

  return null;
}

/**
 * Format a path segment into a readable label
 *
 * @param segment - Raw path segment to format
 * @returns Formatted, human-readable label
 */
export function formatSegmentLabel(segment: string): string {
  // Handle special route segments
  const routeLabels: Record<string, string> = {
    tables: 'Tables',
    linkedTables: 'Linked Tables',
    versions: 'Versions',
    schemas: 'Schemas',
    settings: 'Settings',
    activity: 'Activity',
    create: 'Create',
    version: 'Version',
    edit: 'Edit',
    update: 'Update',
    workspace: 'Workspace',
    members: 'Members',
    billing: 'Billing',
    notifications: 'Notifications',
    links: 'Links',
    favorites: 'Favorites',
    recent: 'Recent',
    trash: 'Trash',
    tags: 'Tags',
    folder: 'Folder',
  };

  if (routeLabels[segment]) {
    return routeLabels[segment];
  }

  // Capitalize first letter and replace hyphens with spaces
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
